import { SITE_URL, localePath, htmlLang, type Lang as SiteLang } from './i18n';
import {
  EQUIPMENT,
  type EquipmentRecord,
  type EquipmentTranslation,
  type Lang,
} from '../content/equipment/data';

import seoEn from '../content/equipment/seo-content.en.json';
import seoRu from '../content/equipment/seo-content.ru.json';
import seoDe from '../content/equipment/seo-content.de.json';
import seoFr from '../content/equipment/seo-content.fr.json';
import seoEs from '../content/equipment/seo-content.es.json';
import seoPtBR from '../content/equipment/seo-content.pt-BR.json';
import seoJa from '../content/equipment/seo-content.ja.json';
import seoKo from '../content/equipment/seo-content.ko.json';

export type { EquipmentRecord, EquipmentTranslation };
export const equipmentBase = (lang: SiteLang) => localePath(lang, '/armwrestling/equipment');

export interface FaqItem {
  q: string;
  a: string;
}

export interface SeoContent {
  seoIntro?: string;
  buyingMistakes?: string[];
  metaDescription?: string;
  faq?: FaqItem[];
}

const seoByLocale: Record<Lang, Record<string, SeoContent>> = {
  en: seoEn as Record<string, SeoContent>,
  ru: seoRu as Record<string, SeoContent>,
  de: seoDe as Record<string, SeoContent>,
  fr: seoFr as Record<string, SeoContent>,
  es: seoEs as Record<string, SeoContent>,
  'pt-BR': seoPtBR as Record<string, SeoContent>,
  ja: seoJa as Record<string, SeoContent>,
  ko: seoKo as Record<string, SeoContent>,
};

// Same interim-fallback rule as src/lib/exercises.ts: human-reviewed SEO copy
// falls back to English when a locale hasn't been authored yet, but the core
// translated content (name/whatItIs/howToUse etc.) never fakes a locale.
function seoFor(id: string, lang: Lang): SeoContent {
  return seoByLocale[lang]?.[id] || seoByLocale.en[id] || {};
}

export interface ResolvedEquipment {
  id: string;
  slug: string;
  emoji: string;
  category: EquipmentRecord['category'];
  skillLevel: EquipmentRecord['skillLevel'];
  relatedExerciseIds: string[];
  relatedTechniqueIds: string[];
  homeFriendly: boolean;
  t: EquipmentTranslation;
  seo: SeoContent;
  imageUrl: string;
}

// Self-hosted product photo (user-provided). Missing files fall back to the
// emoji tile via <Thumb>. Convention: public/images/equipment/{slug}.jpg
export const imageUrlForEquipment = (e: Pick<EquipmentRecord, 'slug'>): string =>
  `/images/equipment/${e.slug}.jpg`;

function resolve(e: EquipmentRecord, lang: Lang): ResolvedEquipment {
  return {
    id: e.id,
    slug: e.slug,
    emoji: e.emoji,
    category: e.category,
    skillLevel: e.skillLevel,
    relatedExerciseIds: e.relatedExerciseIds,
    relatedTechniqueIds: e.relatedTechniqueIds,
    homeFriendly: e.homeFriendly,
    t: e.translations[lang]!,
    seo: seoFor(e.id, lang),
    imageUrl: imageUrlForEquipment(e),
  };
}

// Only items with a real (non-fallback) translation for `lang` are considered
// "available" in that locale — keeps the index/sitemap/hreflang honest.
export function getEquipment(lang: Lang): ResolvedEquipment[] {
  return EQUIPMENT.filter((e) => e.translations[lang]).map((e) => resolve(e, lang));
}

export function getEquipmentItem(lang: Lang, slug: string): ResolvedEquipment | undefined {
  const e = EQUIPMENT.find((x) => x.slug === slug && x.translations[lang]);
  return e ? resolve(e, lang) : undefined;
}

export function localesForEquipment(id: string): Lang[] {
  const e = EQUIPMENT.find((x) => x.id === id);
  if (!e) return [];
  return (Object.keys(e.translations) as Lang[]).filter((l) => e.translations[l]);
}

// Related items ranked by shared category (higher) then shared technique.
// Caps at 6, excludes self.
export function relatedEquipment(item: ResolvedEquipment, lang: Lang, limit = 6): ResolvedEquipment[] {
  const pool = getEquipment(lang).filter((o) => o.id !== item.id);
  const score = (o: ResolvedEquipment) => {
    const sharedCategory = o.category === item.category;
    const sharedTechnique = o.relatedTechniqueIds.some((t) => item.relatedTechniqueIds.includes(t));
    return (sharedCategory ? 3 : 0) + (sharedTechnique ? 2 : 0);
  };
  return pool
    .map((o) => ({ o, s: score(o) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.o);
}

export function alternatesForEquipment(id: string): { hreflang: string; href: string }[] {
  const locales = localesForEquipment(id);
  const e = EQUIPMENT.find((x) => x.id === id)!;
  const out = locales.map((l) => ({
    hreflang: htmlLang[l],
    href: `${SITE_URL}${localePath(l, `/armwrestling/equipment/${e.slug}`)}`,
  }));
  if (locales.includes('en')) {
    out.push({ hreflang: 'x-default', href: `${SITE_URL}${localePath('en', `/armwrestling/equipment/${e.slug}`)}` });
  }
  return out;
}
